/**
 * Skeleton — shimmer placeholder primitive (issue #429).
 *
 * Renders animated placeholder bars/cards with a translucent
 * mint-on-navy gradient sweep. Pure CSS — no JS animation
 * library, just a single `@keyframes` defined in `index.css`
 * under the `.skeleton-shimmer` class.
 *
 * The shape of every Skeleton must match the shape of the
 * loaded state it replaces — otherwise the swap causes the
 * page to "jump" when the data arrives. Use the named
 * presets (`CaseloadSkeleton`, `KpiGridSkeleton`, etc.)
 * rather than rolling your own `<SkeletonBox>` stack when
 * the shape is a known surface.
 */
import type { CSSProperties, ReactElement, ReactNode } from 'react';

export interface SkeletonBoxProps {
  width?: number | string;
  height?: number | string;
  rounded?: boolean;
  style?: CSSProperties;
  className?: string;
}

/**
 * SkeletonBox — a single shimmer bar. Inline-friendly for
 * composing custom skeletons, though the named presets below
 * are preferred for the common surfaces.
 */
export function SkeletonBox({
  width = '100%',
  height = 16,
  rounded = true,
  style,
  className,
}: SkeletonBoxProps): ReactElement {
  return (
    <div
      className={`skeleton-box${rounded ? ' skeleton-box-rounded' : ''}${className ? ` ${className}` : ''}`}
      style={{ width, height, ...style }}
      aria-hidden="true"
    />
  );
}

export interface SkeletonProps {
  rows?: number;
  children?: ReactNode;
}

/**
 * Generic Skeleton wrapper. Sets `aria-busy` for assistive
 * tech and uses `role="status"` so the screen reader announces
 * "Loading" without flooding the user with verbose updates.
 */
export function Skeleton({ rows = 1, children }: SkeletonProps): ReactElement {
  if (children) {
    return (
      <div className="skeleton" role="status" aria-busy="true" aria-label="Loading">
        {children}
      </div>
    );
  }
  return (
    <div className="skeleton" role="status" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <SkeletonBox key={i} height={20} />
      ))}
    </div>
  );
}

/**
 * CaseloadSkeleton — matches the CaseloadScreen row layout.
 * Renders a heading bar + N student-row placeholders.
 */
export function CaseloadSkeleton({ rows = 4 }: { rows?: number }): ReactElement {
  return (
    <div className="skeleton" role="status" aria-busy="true" aria-label="Loading students">
      <SkeletonBox width="40%" height={28} />
      <SkeletonBox width="60%" height={14} style={{ marginBottom: 16 }} />
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton-caseload-row">
          <SkeletonBox width={44} height={44} rounded={true} />
          <div className="skeleton-caseload-row-body">
            <SkeletonBox width="55%" height={16} />
            <SkeletonBox width="75%" height={12} />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * KpiGridSkeleton — matches the AdminOverviewScreen metric grid.
 */
export function KpiGridSkeleton(): ReactElement {
  return (
    <div className="skeleton" role="status" aria-busy="true" aria-label="Loading metrics">
      <SkeletonBox width="40%" height={28} style={{ marginBottom: 12 }} />
      <div className="metric-grid">
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className="metric">
            <SkeletonBox width="60%" height={12} />
            <SkeletonBox width="40%" height={32} style={{ marginTop: 8 }} />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * FormSkeleton — matches the OrgSettingsScreen settings list.
 */
export function FormSkeleton({ rows = 3 }: { rows?: number }): ReactElement {
  return (
    <div className="skeleton" role="status" aria-busy="true" aria-label="Loading settings">
      <SkeletonBox width="30%" height={28} style={{ marginBottom: 16 }} />
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} style={{ marginBottom: 16 }}>
          <SkeletonBox width="30%" height={12} />
          <SkeletonBox width="70%" height={18} style={{ marginTop: 6 }} />
        </div>
      ))}
    </div>
  );
}

/**
 * ThreadListSkeleton — matches the ParentDashboard threads list.
 */
export function ThreadListSkeleton({ rows = 3 }: { rows?: number }): ReactElement {
  return (
    <div className="skeleton" role="status" aria-busy="true" aria-label="Loading messages">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton-caseload-row">
          <SkeletonBox width={36} height={36} rounded={true} />
          <div className="skeleton-caseload-row-body">
            <SkeletonBox width="50%" height={14} />
            <SkeletonBox width="80%" height={12} />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * BootSkeleton — full-screen skeleton with the brand monogram
 * + a 200px-wide heading bar. Used by the App.tsx bootstrap
 * gate (and anywhere a layout-aware skeleton needs to fit a
 * full-viewport surface).
 *
 * The monogram comes through the `monogram` slot so callers
 * can supply their own (the App.tsx bootstrap imports the
 * shared BrandMonogram).
 */
export function BootSkeleton({ monogram }: { monogram?: ReactNode }): ReactElement {
  return (
    <main className="screen boot skeleton-boot">
      {monogram ?? <SkeletonBox width={64} height={64} rounded={true} />}
      <SkeletonBox width={200} height={28} style={{ marginTop: 24 }} />
      <SkeletonBox width={140} height={14} style={{ marginTop: 8 }} />
    </main>
  );
}
